<template>
  <div class="space-y-[1.6rem]">
    <div
      class="flex flex-wrap md:flex-nowrap items-center justify-center gap-[1.6rem]"
    >
      <OnlineButton
        v-model="online"
        class="shrink-0 md:flex-none md:min-w-[9.5rem]"
      />

      <InPersonButton
        v-model="inPerson"
        class="shrink-0 md:flex-none md:min-w-[9.5rem]"
      />

      <LocationAutocomplete
        v-if="inPerson"
        v-model="location"
        :placeholder="$t('Address, city, country') + '...'"
        class="w-full md:flex-none md:min-w-[16rem] md:max-w-[26rem]"
      />
    </div>

    <div
      class="flex flex-wrap md:flex-nowrap items-center justify-center gap-[1.6rem]"
    >
      <CalendarButton
        v-model="dateFrom"
        class="w-full md:flex-none md:min-w-[22rem] md:max-w-[34rem]"
        @update:modelValue="(value: Date) => { dateFrom = value; }"
      />

      <TherapyFilter
        v-model="therapyObj"
        class="w-full md:flex-none md:min-w-[18rem] md:max-w-[26rem] sm:mr-[6px]"
      />

      <AppButton
        class="w-full sm:mt-4 md:w-auto md:min-w-[11rem] md:mt-0 md:flex-none"
        variant="brand"
        :label="$t('Search')"
        :loading="searchLoading"
        :disabled="searchLoading"
        @click.prevent="emitSearch"
      />
    </div>
  </div>
</template>

<script lang="ts">
import AppButton from "@/components/buttons/AppButton.vue";
import OnlineButton from "@/components/buttons/onlineButton.vue";
import InPersonButton from "@/components/buttons/inPersonButton.vue";
import CalendarButton from "@/components/buttons/calendarButton.vue";
import type { Filters } from "@/types";
import type { PropType } from "vue";
import { defineComponent } from "vue";
import dayjs from "dayjs";
import TherapyFilter from "@/components/search/therapyFilter.vue";
import LocationAutocomplete from "@/components/search/locationAutocomplete.vue";

const DEFAULT_LOCATION_RADIUS_KM = 10;

type LocationData = {
  address?: string;
  lat?: number;
  lng?: number;
  placeId?: string | null;
};

export default defineComponent({
  name: "searchCommonSection",
  components: {
    InPersonButton,
    OnlineButton,
    AppButton,
    CalendarButton,
    TherapyFilter,
    LocationAutocomplete,
  },
  props: {
    modelValue: {
      type: Object as PropType<Filters>,
      required: true,
    },
  },
  emits: ["update:modelValue", "search"],
  data() {
    return {
      online: this.modelValue.online ?? true,
      inPerson: this.modelValue.inPerson ?? true,
      location: { ...this.modelValue.location } as LocationData,
      therapy: this.modelValue.therapy || "",
      therapyType: this.modelValue.therapyType || "",
      dateFrom: this.modelValue.dateFrom || new Date(),
      therapyObj: {
        uId: this.modelValue.therapy,
        type: this.modelValue.therapyType,
      },
      suppressTherapySync: false,
      suppressLocationSync: false,
      suppressModeSync: false,
      searchLoading: false,
    };
  },
  methods: {
    normalizeMode(trigger: string | null) {
      if (this.suppressModeSync) {
        return;
      }

      this.suppressModeSync = true;

      let nextOnline = this.online === true;
      let nextInPerson = this.inPerson === true;

      if (nextOnline && nextInPerson) {
        if (trigger === "online") {
          nextInPerson = false;
        } else if (trigger === "inPerson") {
          nextOnline = false;
        } else {
          nextInPerson = false;
        }
      } else if (!nextOnline && !nextInPerson) {
        nextOnline = true;
      }

      this.online = nextOnline;
      this.inPerson = nextInPerson;

      this.suppressModeSync = false;
    },
    updateModelValue() {
      const hasLocation =
        this.location &&
        typeof this.location.lat === "number" &&
        typeof this.location.lng === "number";
      const radiusKm =
        !this.online && hasLocation ? DEFAULT_LOCATION_RADIUS_KM : null;

      this.$emit("update:modelValue", {
        location: this.location,
        online: this.online,
        inPerson: this.inPerson,
        dateFrom: this.dateFrom || new Date(),
        therapy: this.therapy,
        therapyType: this.therapyType,
        radiusKm,
      });
    },
    emitSearch() {
      if (this.searchLoading) {
        return;
      }
      this.searchLoading = true;

      const normalizedLocation =
        this.location &&
        typeof this.location.lat === "number" &&
        typeof this.location.lng === "number"
          ? this.location
          : { address: "", lat: null, lng: null, placeId: null };

      const filtersValue = {
        location: normalizedLocation,
        online: this.online,
        inPerson: this.inPerson,
        dateFrom: this.dateFrom || new Date(),
        therapy: this.therapy,
        therapyType: this.therapyType,
      };

      const hasLocation =
        filtersValue.location &&
        typeof filtersValue.location.lat === "number" &&
        typeof filtersValue.location.lng === "number";
      const radiusKm =
        !filtersValue.online && hasLocation ? DEFAULT_LOCATION_RADIUS_KM : null;
      const pDateFrom = dayjs(this.dateFrom).format(
        process.env.URL_PARAM_DATE_FORMAT,
      );

      this.$router
        .push({
          path: "/search",
          query: {
            lat: filtersValue.location?.lat?.toString(),
            lng: filtersValue.location?.lng?.toString(),
            address: filtersValue.location?.address,
            online: filtersValue.online ? "1" : "0",
            inPerson: filtersValue.inPerson ? "1" : "0",
            radius:
              typeof radiusKm === "number" ? radiusKm.toString() : undefined,
            dateFrom: pDateFrom,
            therapy: filtersValue.therapy || undefined,
            therapyType: filtersValue.therapyType || undefined,
          },
        })
        .then(() => {
          this.$emit("update:modelValue", {
            location: hasLocation
              ? normalizedLocation
              : { address: "", lat: null, lng: null, placeId: null },
            online: this.online,
            inPerson: this.inPerson,
            dateFrom: this.dateFrom || new Date(),
            therapy: this.therapy,
            therapyType: this.therapyType,
            radiusKm,
          });
          this.$emit("search");
        })
        .catch((error) => {
          void error;
        })
        .finally(() => {
          this.searchLoading = false;
        });
    },
  },
  watch: {
    online() {
      if (this.suppressModeSync) {
        return;
      }
      //this.normalizeMode("online");
      this.updateModelValue();
    },
    inPerson() {
      if (this.suppressModeSync) {
        return;
      }
      // this.normalizeMode("inPerson");
      this.updateModelValue();
    },
    therapyObj: {
      handler(newVal) {
        if (this.suppressTherapySync) {
          this.suppressTherapySync = false;
          return;
        }
        if (newVal && typeof newVal === "object") {
          this.therapy = newVal.uId || "";
          this.therapyType = newVal.type || "";
        } else {
          this.therapy = "";
          this.therapyType = "";
        }
        this.updateModelValue();
      },
      deep: true,
    },
    modelValue: {
      handler(newVal) {
        if (!newVal) {
          return;
        }
        this.suppressLocationSync = true;
        this.online = newVal.online ?? true;
        this.inPerson = newVal.inPerson ?? false;
        this.location = { ...newVal.location } as LocationData;
        this.therapy = newVal.therapy || "";
        this.therapyType = newVal.therapyType || "";
        this.dateFrom = newVal.dateFrom || new Date();
        this.suppressTherapySync = true;
        this.therapyObj = { uId: newVal.therapy, type: newVal.therapyType };
        // this.normalizeMode(null);
        this.$nextTick(() => {
          this.suppressTherapySync = false;
          this.suppressLocationSync = false;
        });
      },
      deep: true,
    },
    location: {
      handler() {
        if (this.suppressLocationSync) {
          return;
        }
        this.updateModelValue();
      },
      deep: true,
    },
  },
});
</script>

<style scoped></style>
